import { FileUp, KeyRound, Link2, ListVideo, LoaderCircle } from "lucide-react";
import { useRef, useState, type DragEvent, type FormEvent } from "react";

type SourceMode = "youtube" | "links" | "file";

interface SourcePanelProps {
  apiKeyConfigured: boolean;
  isLoading: boolean;
  loadedCount: number;
  onOpenSettings: () => void;
  onLoadYouTube: (value: string, append: boolean) => Promise<void>;
  onPasteLinks: (value: string, append: boolean) => void;
  onImportFile: (file: File, append: boolean) => Promise<void>;
}

const sourceModes: Array<{ id: SourceMode; label: string; icon: typeof ListVideo }> = [
  { id: "youtube", label: "YouTube", icon: ListVideo },
  { id: "links", label: "Paste links", icon: Link2 },
  { id: "file", label: "Import file", icon: FileUp },
];

export function SourcePanel({
  apiKeyConfigured,
  isLoading,
  loadedCount,
  onOpenSettings,
  onLoadYouTube,
  onPasteLinks,
  onImportFile,
}: SourcePanelProps) {
  const [mode, setMode] = useState<SourceMode>("youtube");
  const [youtubeInput, setYoutubeInput] = useState("");
  const [linksInput, setLinksInput] = useState("");
  const [append, setAppend] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const handleYouTubeSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!youtubeInput.trim()) return;
    await onLoadYouTube(youtubeInput, append);
  };

  const handleLinksSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!linksInput.trim()) return;
    onPasteLinks(linksInput, append);
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    await onImportFile(file, append);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    await handleFile(event.dataTransfer.files[0]);
  };

  return (
    <section aria-labelledby="source-title" className="flex h-full min-h-0 flex-col bg-paper lg:border-r lg:border-line">
      <div className="border-b border-line px-4 py-4">
        <p className="eyebrow">Source / input</p>
        <h2 id="source-title" className="mt-1 text-lg font-semibold text-ink">Bring media in</h2>
      </div>

      <div className="grid grid-cols-3 border-b border-line" role="tablist" aria-label="Playlist source">
        {sourceModes.map(({ id, label, icon: Icon }) => (
          <button
            className={`flex min-h-11 cursor-pointer items-center justify-center gap-1.5 border-b-2 px-2 text-xs font-semibold transition-colors ${mode === id ? "border-accent text-accent" : "border-transparent text-muted hover:bg-canvas hover:text-ink"}`}
            type="button"
            role="tab"
            aria-label={`${label} source`}
            aria-selected={mode === id}
            key={id}
            onClick={() => setMode(id)}
          >
            <Icon aria-hidden="true" size={15} strokeWidth={1.8} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        {mode === "youtube" ? (
          <form className="space-y-5" onSubmit={(event) => void handleYouTubeSubmit(event)}>
            <div>
              <label className="field-label" htmlFor="youtube-playlist">YouTube playlist URL or ID</label>
              <textarea
                id="youtube-playlist"
                className="field min-h-24 resize-y"
                placeholder="https://youtube.com/playlist?list=..."
                value={youtubeInput}
                onChange={(event) => setYoutubeInput(event.target.value)}
                disabled={isLoading}
              />
              <p className="field-help">Public and unlisted playlists work. Private playlists require authorization this app does not request.</p>
            </div>

            <div className={`flex items-start gap-2.5 border px-3 py-3 text-sm ${apiKeyConfigured ? "border-valid/35 bg-valid-soft text-valid" : "border-warning/40 bg-warning-soft text-warning"}`}>
              <KeyRound aria-hidden="true" className="mt-0.5 shrink-0" size={16} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{apiKeyConfigured ? "Local API key ready" : "YouTube API key required"}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">Stored only in this browser. It is sent directly to Google when you load YouTube data.</p>
              </div>
              <button className="text-button shrink-0" type="button" onClick={onOpenSettings}>Settings</button>
            </div>

            <button className="primary-button w-full" type="submit" disabled={isLoading || !youtubeInput.trim()}>
              {isLoading ? <LoaderCircle aria-hidden="true" className="animate-spin motion-reduce:animate-none" size={17} /> : <ListVideo aria-hidden="true" size={17} />}
              {isLoading ? `Loaded ${loadedCount}` : "Load playlist"}
            </button>
          </form>
        ) : null}

        {mode === "links" ? (
          <form className="space-y-5" onSubmit={handleLinksSubmit}>
            <div>
              <label className="field-label" htmlFor="media-links">Media URLs</label>
              <textarea
                id="media-links"
                className="field min-h-52 resize-y font-mono text-xs leading-6"
                placeholder={"One URL per line\nOptional title | https://example.com/video.mp4"}
                value={linksInput}
                onChange={(event) => setLinksInput(event.target.value)}
              />
              <p className="field-help">Use <code>Title | URL</code> to name a link now, or edit it later.</p>
            </div>
            <button className="primary-button w-full" type="submit" disabled={!linksInput.trim()}>
              <Link2 aria-hidden="true" size={17} /> Add links
            </button>
          </form>
        ) : null}

        {mode === "file" ? (
          <div className="space-y-5">
            <div
              className={`grid min-h-56 place-items-center border border-dashed px-5 text-center transition-colors ${dragActive ? "border-accent bg-accent-soft" : "border-line-strong bg-canvas"}`}
              onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragActive(false)}
              onDrop={(event) => void handleDrop(event)}
            >
              <div>
                <FileUp aria-hidden="true" className="mx-auto text-accent" size={26} strokeWidth={1.6} />
                <p className="mt-3 font-semibold text-ink">Drop a ProTV playlist</p>
                <p className="mx-auto mt-1 max-w-56 text-xs leading-relaxed text-muted">Tokenized and JSON `.playlist` files are supported, including legacy 2.2 and 2.3 title syntax.</p>
                <button className="secondary-button mt-4" type="button" onClick={() => fileInput.current?.click()}>Choose file</button>
                <input
                  ref={fileInput}
                  className="sr-only"
                  type="file"
                  aria-label="Choose a ProTV playlist file"
                  accept=".playlist,.txt,.json,text/plain,application/json"
                  onChange={(event) => void handleFile(event.target.files?.[0])}
                />
              </div>
            </div>
          </div>
        ) : null}

        <label className="mt-6 flex cursor-pointer items-start gap-2.5 border-t border-line pt-4 text-sm text-ink">
          <input aria-label="Add to current playlist" className="mt-0.5 size-4 accent-accent" type="checkbox" checked={append} onChange={(event) => setAppend(event.target.checked)} />
          <span><span className="font-semibold">Add to current playlist</span><span className="mt-0.5 block text-xs text-muted">Leave off to replace the current entries.</span></span>
        </label>
      </div>
    </section>
  );
}
